import { Box } from "@mui/material";
import { type FunctionComponent } from "react";
import { Handle, Position } from "reactflow";
import type InputItem from "../features/InputItem";

interface CollapsedInputHandlesProps {
  inputItems: InputItem[];
  handleSize: number;
  handleHoverScale: number;
  handleColor: string;
  handleHoverColor: string;
  handleBorderColor: string;
}

/**
 * The target handles of a collapsed node.
 *
 * A collapsed node hides its input rows, but the handles that live in those
 * rows can't go with them: React Flow looks the target handle up by ID to
 * place the edge, and an edge pointing at a handle that isn't mounted simply
 * disappears from the canvas. So the handles move to the left edge of the
 * header instead, spread out evenly so that each port is still its own drop
 * target.
 *
 * Only ports that show a handle when expanded get one here, which leaves
 * constant and variable nodes without any, exactly as before.
 */
const CollapsedInputHandles: FunctionComponent<CollapsedInputHandlesProps> = ({
  inputItems,
  handleSize,
  handleHoverScale,
  handleColor,
  handleHoverColor,
  handleBorderColor,
}) => {
  const handleItems = inputItems.filter((item) => item.showHandle);
  if (handleItems.length === 0) {
    return null;
  }

  return (
    <Box
      // Zero width so the dots sit on the frame's left border rather than
      // pushing the header contents to the right
      position="absolute"
      top={0}
      bottom={0}
      left={0}
      width={0}
      sx={{
        "& .react-flow__handle": {
          transition:
            "transform 120ms ease-out, background-color 120ms ease-out",
        },
        "& .react-flow__handle:hover": {
          backgroundColor: `${handleHoverColor} !important`,
          transform: `translateY(-50%) scale(${handleHoverScale})`,
        },
      }}
    >
      {handleItems.map((item, index) => (
        <Handle
          key={item.id}
          id={item.id}
          position={Position.Left}
          style={{
            position: "absolute",
            background: handleColor,
            border: `2px solid ${handleBorderColor}`,
            borderRadius: "50%",
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.3)",
            // Splits the left edge into equal slots, one per port, so two
            // ports land at 1/3 and 2/3 instead of on top of each other
            top: `${((index + 1) / (handleItems.length + 1)) * 100}%`,
            left: -handleSize / 2,
            transform: "translateY(-50%)",
            width: handleSize,
            height: handleSize,
          }}
          type="target"
        />
      ))}
    </Box>
  );
};

export default CollapsedInputHandles;
